import { motion } from "framer-motion";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useEffect, useState, useRef } from "react";
import { ArrowRight } from "lucide-react";

const statsData = [
  { value: 19,   suffix: "+", label: "Global Offices",      gradient: "from-emerald-400 to-teal-600"   },
  { value: 14,   suffix: "+", label: "Years of Excellence", gradient: "from-blue-400 to-indigo-600" },
  { value: 1711, suffix: "+", label: "Trusted Members",     gradient: "from-emerald-400 to-teal-600" },
  { value: 3331, suffix: "+", label: "Cases Solved",        gradient: "from-violet-400 to-purple-600" },
];

function useCountUp(target, duration = 1800) {
  const [count, setCount] = useState(0);
  const started = useRef(false);
  const ref = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && !started.current) {
          started.current = true;
          let start = 0;
          const step = Math.ceil(target / (duration / 16));
          const id = setInterval(() => {
            start = Math.min(start + step, target);
            setCount(start);
            if (start >= target) clearInterval(id);
          }, 16);
        }
      },
      { threshold: 0.4 }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, [target, duration]);

  return { count, ref };
}

const StatCard = ({ stat }) => {
  const { count, ref } = useCountUp(stat.value);
  return (
    <motion.div
      ref={ref}
      initial={{ opacity: 0, y: 24 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ duration: 0.5 }}
      className="relative bg-white rounded-2xl border border-slate-100 shadow-sm hover:shadow-md transition-shadow duration-300 overflow-hidden p-6 text-center shimmer-card"
    >
      <div className={`absolute top-0 left-0 right-0 h-1 bg-gradient-to-r ${stat.gradient}`} />
      <p className={`text-4xl font-extrabold text-transparent bg-clip-text bg-gradient-to-r ${stat.gradient} mb-1 tabular-nums`}>
        {count}{stat.suffix}
      </p>
      <p className="text-sm font-semibold text-slate-600">{stat.label}</p>
    </motion.div>
  );
};

const AboutAiraComponent = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();

  return (
    <section className="py-8">
      <div className="flex flex-col gap-8">

        {/* Text block */}
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
        >
          <span className="inline-block text-xs font-bold uppercase tracking-widest text-emerald-600 mb-3">About AIRAA</span>
          <h2 className="text-3xl md:text-4xl font-extrabold text-blue-950 leading-tight mb-5">
            {t("aboutTitle")}{" "}
            <span className="text-emerald-500">{t("aboutTitle2")}</span>
          </h2>
          <div className="space-y-3 text-slate-600 text-base leading-relaxed">
            <p>
              Founded in 2011, AIRAA (All India Recruitment Agency Association) represents 1711+ recruitment agencies across India, working to enhance ethical, transparent, and sustainable employment opportunities for Indian workers globally.
            </p>
            <p>
              We collaborate with the government and international stakeholders to shape policies that benefit recruitment agencies and job seekers. Our initiatives focus on skill development, industry promotion, and workforce mobility.
            </p>
            <p>
              By connecting overseas employers with skilled Indian talent, AIRAA strengthens global workforce mobility while advocating for fair employment practices and worker welfare.
            </p>
          </div>
          <button
            onClick={() => navigate("/about-airaa")}
            className="mt-6 inline-flex items-center gap-2 bg-emerald-700 hover:bg-emerald-800 text-white text-sm font-bold px-6 py-2.5 rounded-full shadow-md shadow-emerald-700/25 transition-all duration-200 hover:scale-105 active:scale-95"
          >
            {t("learnMore")} <ArrowRight size={14} />
          </button>
        </motion.div>

        {/* Stats grid */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          {statsData.map((stat, i) => <StatCard key={i} stat={stat} />)}
        </div>
      </div>
    </section>
  );
};

export default AboutAiraComponent;
